import { CATEGORY_COLORS } from '../../constants/colors';
import { getMostDistinctColor } from '../../../FlowchartView/utils/statusUtils';

/**
 * Merge pasted tasks (and their tag/custom-field definitions) into a sprint snapshot.
 * Pure function — safe to call outside React setState updaters.
 */
export function mergePastedTasksIntoSprint(prev, { pastedTasks, tagDefinitions = [], fieldsToAdd = [] }) {
  if (!prev) return prev;

  const existingTasks = prev.tasks || [];

  const tasksWithOrder = pastedTasks.map((task, idx) => {
    const {
      position: _position,
      _originalPosition,
      _clipboardPos,
      _campaignOffset,
      ...rest
    } = task;
    // Keep explicit paste-here / paste-connected coords so tidy cannot yank
    // the cards to the top. Ctrl+V still arrives without x/y.
    const keepX = Number.isFinite(task.x);
    const keepY = Number.isFinite(task.y);
    return {
      ...rest,
      ...(keepX ? { x: task.x } : { x: undefined }),
      ...(keepY ? { y: task.y } : { y: undefined }),
      order: existingTasks.length + idx + 1,
    };
  });

  let updatedTags = prev.tags || prev.categories || [];
  const existingTagNames = new Set(updatedTags.map(t => t.name));

  const pastedTagNames = new Set();
  pastedTasks.forEach(task => {
    if (Array.isArray(task.tags)) {
      task.tags.forEach(tag => pastedTagNames.add(tag));
    } else if (task.category) {
      pastedTagNames.add(task.category);
    }
  });

  for (const tagName of pastedTagNames) {
    if (!existingTagNames.has(tagName)) {
      const clipboardTag = tagDefinitions.find(t => t.name === tagName);
      const color = clipboardTag?.color || getMostDistinctColor(
        updatedTags.map(t => t.color).filter(Boolean),
        CATEGORY_COLORS
      );
      updatedTags = [...updatedTags, { name: tagName, color }];
    }
  }

  let updatedCustomFields = prev.customFields || [];
  const existingFieldIds = new Set(updatedCustomFields.map(f => f.id));

  for (const fieldToAdd of fieldsToAdd) {
    if (!existingFieldIds.has(fieldToAdd.id)) {
      const newOrder = updatedCustomFields.length;
      updatedCustomFields = [...updatedCustomFields, {
        ...fieldToAdd,
        order: newOrder,
      }];
      console.log(`📋 Added custom field "${fieldToAdd.name}" from clipboard`);
    }
  }

  return {
    ...prev,
    tags: updatedTags,
    categories: updatedTags,
    customFields: updatedCustomFields,
    tasks: [...existingTasks, ...tasksWithOrder],
  };
}
